import { useCallback, useEffect, useState } from 'react';
import {
  assignSupervisors, listCampaignStores, listSupervisorAssignments, listSupervisors,
  unassignSupervisor,
  type CampaignStore, type SupervisorAssignment, type SupervisorOption,
} from '../../api/campaigns';

/**
 * Asignar supervisores a tiendas de la campaña (tienda por tienda o por cadena entera) y ver,
 * por supervisor, qué tiendas tiene — spec 2026-09-24-supervisores-por-tienda-design.md.
 *
 * "Por cadena" es un ATAJO que llena tienda por tienda en el momento de asignar: no es una
 * regla que se aplique sola a una tienda que se agregue después a esa cadena. Si hace falta
 * cubrir una tienda nueva, se asigna a mano — igual que hoy nadie hereda nada automáticamente.
 */
export default function CampaniaSupervisores({ campaignId, soloLectura }: {
  campaignId: string;
  soloLectura: boolean;
}) {
  const [tiendas, setTiendas] = useState<CampaignStore[]>([]);
  const [supervisores, setSupervisores] = useState<SupervisorOption[]>([]);
  const [asignaciones, setAsignaciones] = useState<SupervisorAssignment[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modo, setModo] = useState<'tienda' | 'cadena'>('tienda');
  const [pdvId, setPdvId] = useState('');
  const [cadena, setCadena] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [quitandoId, setQuitandoId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [t, s, a] = await Promise.all([
        listCampaignStores(campaignId),
        listSupervisors(),
        listSupervisorAssignments(campaignId),
      ]);
      setTiendas(t);
      setSupervisores(s);
      setAsignaciones(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la información');
    } finally {
      setCargando(false);
    }
  }, [campaignId]);

  useEffect(() => { void load(); }, [load]);

  const cadenas = [...new Set(
    tiendas.map((t) => t.chain).filter((c): c is string => c !== null),
  )].sort();
  const porTienda = new Map(asignaciones.map((a) => [a.pdv_id, a]));
  const sinSupervisor = tiendas.filter((t) => !porTienda.has(t.id));

  async function asignar() {
    setError(null);
    if (!supervisorId) { setError('Elige un supervisor.'); return; }
    const objetivo = modo === 'tienda'
      ? (pdvId ? [pdvId] : [])
      : tiendas.filter((t) => t.chain === cadena).map((t) => t.id);
    if (objetivo.length === 0) {
      setError(modo === 'tienda' ? 'Elige una tienda.' : 'Elige una cadena.');
      return;
    }

    // Confirmar antes de pisar a un supervisor DISTINTO que ya estaba puesto. Reasignar al
    // mismo supervisor que ya tenía la tienda no pide confirmación: no cambia nada.
    const pisa = objetivo
      .map((id) => porTienda.get(id))
      .filter((a): a is SupervisorAssignment => a !== undefined && a.profile_id !== supervisorId);
    if (pisa.length > 0) {
      const nombres = pisa
        .map((a) => `${a.pdv.name} (hoy: ${a.profile.first_names} ${a.profile.last_names})`)
        .join('\n');
      if (!window.confirm(`Esto reemplaza al supervisor actual en:\n\n${nombres}\n\n¿Continuar?`)) {
        return;
      }
    }

    setGuardando(true);
    try {
      await assignSupervisors(campaignId, objetivo, supervisorId);
      setPdvId('');
      setCadena('');
      setSupervisorId('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo asignar');
    } finally {
      setGuardando(false);
    }
  }

  async function quitar(a: SupervisorAssignment) {
    if (!window.confirm(`¿Quitar a ${a.profile.first_names} ${a.profile.last_names} de ${a.pdv.name}?`)) {
      return;
    }
    setError(null);
    setQuitandoId(a.id);
    try {
      await unassignSupervisor(a.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar la asignación');
    } finally {
      setQuitandoId(null);
    }
  }

  if (cargando) return <p className="text-sm text-gray-500">Cargando…</p>;

  const porSupervisor = new Map<string, { nombre: string; tiendas: SupervisorAssignment[] }>();
  for (const a of asignaciones) {
    if (!porSupervisor.has(a.profile_id)) {
      porSupervisor.set(a.profile_id, {
        nombre: `${a.profile.first_names} ${a.profile.last_names}`,
        tiendas: [],
      });
    }
    porSupervisor.get(a.profile_id)?.tiendas.push(a);
  }

  return (
    <section className="border rounded p-4 bg-white">
      <h2 className="font-semibold mb-3">Supervisores por tienda</h2>
      {error && <p className="text-red-600 mb-2">{error}</p>}

      {!soloLectura && (
        <div className="mb-4 rounded border p-3 bg-gray-50">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-sm">
              <span className="block mb-1">Asignar por</span>
              <select className="border rounded px-2 py-1" value={modo}
                onChange={(e) => setModo(e.target.value as 'tienda' | 'cadena')}>
                <option value="tienda">Una tienda</option>
                <option value="cadena">Toda una cadena</option>
              </select>
            </label>
            {modo === 'tienda' ? (
              <label className="text-sm">
                <span className="block mb-1">Tienda</span>
                <select className="border rounded px-2 py-1" value={pdvId}
                  onChange={(e) => setPdvId(e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {tiendas.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.code} — {t.name}{t.chain ? ` (${t.chain})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="text-sm">
                <span className="block mb-1">Cadena</span>
                <select className="border rounded px-2 py-1" value={cadena}
                  onChange={(e) => setCadena(e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {cadenas.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            )}
            <label className="text-sm">
              <span className="block mb-1">Supervisor</span>
              <select className="border rounded px-2 py-1" value={supervisorId}
                onChange={(e) => setSupervisorId(e.target.value)}>
                <option value="">— Selecciona —</option>
                {supervisores.map((s) => (
                  <option key={s.id} value={s.id}>{s.first_names} {s.last_names}</option>
                ))}
              </select>
            </label>
            <button className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
              disabled={guardando} onClick={() => void asignar()}>
              {guardando ? 'Asignando…' : 'Asignar'}
            </button>
          </div>
        </div>
      )}

      {porSupervisor.size === 0 && (
        <p className="text-sm text-gray-500 mb-3">Todavía no hay supervisores asignados.</p>
      )}
      {[...porSupervisor.entries()].map(([id, { nombre, tiendas: ts }]) => (
        <div key={id} className="mb-3">
          <h3 className="font-medium text-sm">
            {nombre} <span className="text-gray-500">
              ({ts.length} tienda{ts.length === 1 ? '' : 's'})
            </span>
          </h3>
          <ul className="text-sm text-gray-700 list-disc pl-5">
            {ts.map((a) => (
              <li key={a.id} className="flex items-center gap-2">
                <span>{a.pdv.code} — {a.pdv.name}{a.pdv.chain ? ` · ${a.pdv.chain}` : ''}</span>
                {!soloLectura && (
                  <button className="text-red-600 underline text-xs disabled:opacity-50"
                    disabled={quitandoId === a.id} onClick={() => void quitar(a)}>
                    {quitandoId === a.id ? 'Quitando…' : 'Quitar'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* El hueco queda a la vista, igual que el calendario de cobertura de Softys: un número
          en cero no es un estado, y esconder las tiendas sin supervisor sería lo mismo que
          decir que están todas cubiertas. */}
      <div className="mt-4 pt-3 border-t">
        <h3 className="font-medium text-sm text-amber-700">
          Sin supervisor ({sinSupervisor.length} de {tiendas.length})
        </h3>
        {sinSupervisor.length === 0 ? (
          <p className="text-sm text-gray-500">Todas las tiendas de esta campaña tienen supervisor.</p>
        ) : (
          <ul className="text-sm text-gray-700 list-disc pl-5">
            {sinSupervisor.map((t) => (
              <li key={t.id}>{t.code} — {t.name}{t.chain ? ` · ${t.chain}` : ''}</li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
